// src/components/RichiediOfferteNF.js
// «📨 Richiedi offerte» dalla NF (W436): si scelgono uno o più fornitori dell'anagrafica e
// per ciascuno nasce una richiesta d'offerta (RdO) con le righe della NF, senza prezzi.
// Chi ha l'email in anagrafica la riceve subito da Nexiora a nome dell'azienda (le risposte
// tornano a chi la invia); per gli altri resta «Da inviare», da mandare a mano e segnare.
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";
import { messaggioErrore } from "../utils/erroriUtente";
import { creaRdo, inviaRdoEmail } from "../utils/azioniAcquisti";
import { rdoDaNf, testoRichiestaOfferta } from "../utils/rdo";

export default function RichiediOfferteNF({ nf, orgId, currentUser, giaRichiesti = [], onChiudi, onFatto }) {
  const { addToast } = useToast();
  const [fornitori, setFornitori] = useState([]);
  const [scelti, setScelti] = useState(() => new Set(nf.fornitoreId && !giaRichiesti.includes(nf.fornitoreId) ? [nf.fornitoreId] : []));
  const [cerca, setCerca] = useState("");
  const [invia, setInvia] = useState(true);
  const [lavoro, setLavoro] = useState(false);
  useEffect(() => onSnapshot(query(collection(db, "fornitori"), where("organizationId", "==", orgId)),
    s => setFornitori(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(f => !f.eliminato && f.denominazione).sort((a, b) => a.denominazione.localeCompare(b.denominazione, "it"))),
    () => setFornitori([])), [orgId]);
  const visibili = fornitori.filter(f => !cerca || `${f.denominazione} ${f.categoria || ""} ${f.email || ""}`.toLowerCase().includes(cerca.toLowerCase()));
  const anteprima = useMemo(() => testoRichiestaOfferta(rdoDaNf(nf, {}, {})), [nf]);
  const cambia = (id) => setScelti(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const conferma = async () => {
    const lista = fornitori.filter(f => scelti.has(f.id));
    if (!lista.length) { addToast("Scegli almeno un fornitore", "error"); return; }
    setLavoro(true);
    try {
      const nuove = await creaRdo(nf, lista, { orgId, utente: currentUser });
      let inviate = 0, nonInviate = [];
      if (invia) {
        for (const r of nuove) {
          if (!r.fornitoreEmail) { nonInviate.push(`${r.fornitoreNome} (senza email)`); continue; }
          try { await inviaRdoEmail(r); inviate++; } catch (e) { nonInviate.push(`${r.fornitoreNome} (${messaggioErrore(e)})`); }
        }
      }
      addToast(invia
        ? `${nuove.length} richieste preparate, ${inviate} inviate per email${nonInviate.length ? ` · da mandare a mano: ${nonInviate.join(", ")}` : ""}`
        : `${nuove.length} richieste preparate: le trovi nella scheda RdO, da inviare`, nonInviate.length ? "info" : "success");
      onFatto?.(nuove);
      onChiudi();
    } catch (e) { addToast(messaggioErrore(e, "preparare le richieste d'offerta"), "error"); }
    setLavoro(false);
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onChiudi()}>
      <div className="modal" role="dialog" aria-label="Richiedi offerte" style={{ maxWidth: 680, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>📨 Richiedi offerte — NF #{nf.numero}</h3>
          <button type="button" className="btn-icon" onClick={onChiudi} aria-label="Chiudi" style={{ marginLeft: "auto" }}>✕</button>
        </div>
        <div style={{ fontSize: 12.5, color: "#5b21b6", background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "8px 12px", marginBottom: 12, lineHeight: 1.5 }}>
          💜 Scegli i fornitori a cui chiedere il prezzo: ognuno riceve la sua richiesta con le righe della NF, il luogo e la data di consegna. Mai prezzi né dati interni. Per un acquisto importante conviene chiederne 2 o 3.
        </div>
        <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="🔍 Cerca fornitore…" aria-label="Cerca fornitore"
          style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, marginBottom: 8, boxSizing: "border-box", fontFamily: "inherit" }}/>
        <div role="group" aria-label="Fornitori" style={{ border: "1px solid #e5e7eb", borderRadius: 8, maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
          {visibili.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: "#9ca3af" }}>{fornitori.length ? "Nessun fornitore trovato" : "Nessun fornitore in anagrafica: aggiungili nella scheda Fornitori"}</div>}
          {visibili.map(f => {
            const gia = giaRichiesti.includes(f.id);
            return (
              <label key={f.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #f3f4f6", cursor: gia ? "default" : "pointer", opacity: gia ? .55 : 1 }}>
                <input type="checkbox" checked={scelti.has(f.id)} disabled={gia} onChange={() => cambia(f.id)}/>
                <span style={{ flex: 1, fontSize: 13 }}><b>{f.denominazione}</b>{f.categoria ? <span style={{ color: "#9ca3af" }}> · {f.categoria}</span> : null}</span>
                <span style={{ fontSize: 11.5, color: f.email ? "#6b7280" : "#b45309" }}>{gia ? "già richiesta" : f.email || "⚠️ senza email"}</span>
              </label>
            );
          })}
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 10 }}>
          <input type="checkbox" checked={invia} onChange={e => setInvia(e.target.checked)}/>
          Invia subito per email ai fornitori che hanno l'indirizzo (le risposte arrivano a te{currentUser?.email ? `, ${currentUser.email}` : ""})
        </label>
        <details style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          <summary style={{ cursor: "pointer" }}>Cosa riceve il fornitore</summary>
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: 10, marginTop: 6 }}>{anteprima.replace(/\t/g, "   ")}</pre>
        </details>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-secondary" onClick={onChiudi}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={lavoro || !scelti.size} onClick={conferma}>
            {lavoro ? "Preparazione…" : `${invia ? "Prepara e invia" : "Prepara"} ${scelti.size ? `(${scelti.size})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
